import { useEffect, useState, type FormEvent } from "react";
import { useTenantAuth } from "../../context/auth";
import { useTranslation } from "react-i18next";

export function ManagerSettingsPage() {
  const { t } = useTranslation();
  const { bootstrap, updateTenantMessagingStrategy } = useTenantAuth();
  const [marketProfile, setMarketProfile] = useState<"default" | "telegram_first" | "whatsapp_first">("default");
  const [primaryMessenger, setPrimaryMessenger] = useState<"" | "telegram" | "whatsapp" | "sms" | "email">("");
  const [messengerFirstMode, setMessengerFirstMode] = useState(false);
  const [savingStrategy, setSavingStrategy] = useState(false);
  const [strategyNotice, setStrategyNotice] = useState<string>("");
  const [strategyError, setStrategyError] = useState<string>("");

  useEffect(() => {
    setMarketProfile((bootstrap?.tenant.market_profile as "default" | "telegram_first" | "whatsapp_first") ?? "default");
    setPrimaryMessenger((bootstrap?.tenant.primary_messenger as "telegram" | "whatsapp" | "sms" | "email" | null) ?? "");
    setMessengerFirstMode(Boolean(bootstrap?.tenant.messenger_first_mode));
  }, [
    bootstrap?.tenant.market_profile,
    bootstrap?.tenant.primary_messenger,
    bootstrap?.tenant.messenger_first_mode,
  ]);

  const channelCapabilities = Object.entries(bootstrap?.channel_capabilities ?? {});

  async function onSaveMessagingStrategy(event: FormEvent) {
    event.preventDefault();
    setSavingStrategy(true);
    setStrategyError("");
    setStrategyNotice("");
    try {
      await updateTenantMessagingStrategy({
        market_profile: marketProfile,
        primary_messenger: primaryMessenger || null,
        messenger_first_mode: messengerFirstMode,
        apply_profile_defaults: false,
      });
      setStrategyNotice(t("manager.settings.strategySaved"));
    } catch (error) {
      setStrategyError(error instanceof Error ? error.message : t("manager.settings.saveFailed"));
    } finally {
      setSavingStrategy(false);
    }
  }

  return (
    <div className="grid gap-4">
      <h1 className="text-2xl font-semibold">{t("manager.settings.title")}</h1>
      <section className="panel p-4">
        <h2 className="text-lg font-semibold">{t("manager.settings.tenantProfile")}</h2>
        <div className="text-sm mt-2 grid gap-1 break-words">
          <p>{t("manager.settings.field.name")}: {bootstrap?.tenant.name}</p>
          <p>{t("manager.settings.field.slug")}: {bootstrap?.tenant.slug}</p>
          <p>{t("manager.settings.field.status")}: {bootstrap?.tenant.status}</p>
          <p>{t("manager.settings.field.locale")}: {bootstrap?.tenant.default_locale}</p>
          <p>{t("manager.settings.field.plan")}: {bootstrap?.tenant.plan_code || t("manager.settings.notSet")}</p>
        </div>
      </section>
      <section className="panel p-4">
        <h2 className="text-lg font-semibold">{t("manager.settings.messagingStrategy")}</h2>
        <p className="text-sm text-slate-600 mt-1">{t("manager.settings.messagingStrategyHelp")}</p>
        <form className="mt-4 grid gap-4" onSubmit={onSaveMessagingStrategy}>
          <label className="grid gap-1 text-sm">
            <span>{t("manager.settings.field.marketProfile")}</span>
            <select
              className="border rounded px-3 py-2 bg-white"
              value={marketProfile}
              onChange={(e) => setMarketProfile(e.target.value as "default" | "telegram_first" | "whatsapp_first")}
              aria-label={t("manager.settings.field.marketProfile")}
            >
              <option value="default">{t("manager.settings.marketProfile.default")}</option>
              <option value="telegram_first">{t("manager.settings.marketProfile.telegram_first")}</option>
              <option value="whatsapp_first">{t("manager.settings.marketProfile.whatsapp_first")}</option>
            </select>
          </label>
          <label className="grid gap-1 text-sm">
            <span>{t("manager.settings.field.primaryMessenger")}</span>
            <select
              className="border rounded px-3 py-2 bg-white"
              value={primaryMessenger}
              onChange={(e) => setPrimaryMessenger(e.target.value as "" | "telegram" | "whatsapp" | "sms" | "email")}
              aria-label={t("manager.settings.field.primaryMessenger")}
            >
              <option value="">{t("manager.settings.primaryMessenger.none")}</option>
              <option value="telegram">{t("manager.settings.primaryMessenger.telegram")}</option>
              <option value="whatsapp">{t("manager.settings.primaryMessenger.whatsapp")}</option>
              <option value="sms">{t("manager.settings.primaryMessenger.sms")}</option>
              <option value="email">{t("manager.settings.primaryMessenger.email")}</option>
            </select>
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={messengerFirstMode}
              onChange={(e) => setMessengerFirstMode(e.target.checked)}
              aria-label={t("manager.settings.field.messengerFirstMode")}
            />
            <span>{t("manager.settings.field.messengerFirstMode")}</span>
          </label>
          <div className="flex items-center gap-3">
            <button
              type="submit"
              className="px-3 py-2 rounded bg-slate-900 text-white disabled:opacity-60"
              disabled={savingStrategy}
            >
              {savingStrategy ? t("manager.settings.saving") : t("manager.settings.saveStrategy")}
            </button>
            {strategyNotice ? <span className="text-sm text-emerald-700">{strategyNotice}</span> : null}
            {strategyError ? <span className="text-sm text-rose-700">{strategyError}</span> : null}
          </div>
        </form>
      </section>
      <section className="panel p-4">
        <h2 className="text-lg font-semibold">{t("manager.settings.channelCapabilities")}</h2>
        <div className="grid gap-3 mt-3">
          {channelCapabilities.length === 0 ? (
            <p className="text-sm text-slate-600">{t("manager.settings.noCapabilities")}</p>
          ) : (
            channelCapabilities.map(([key, caps]) => (
              <div key={key} className="border rounded-lg p-3 bg-white">
                <div className="flex items-center justify-between gap-2">
                  <strong>{caps.label}</strong>
                  <span className="text-xs px-2 py-1 rounded bg-slate-100 text-slate-700">{caps.rollout_mode}</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs mt-3 text-slate-700">
                  <div>{t("manager.settings.cap.managerApp")}: {caps.supports_manager_embedded_app ? "yes" : "no"}</div>
                  <div>{t("manager.settings.cap.clientApp")}: {caps.supports_client_embedded_app ? "yes" : "no"}</div>
                  <div>{t("manager.settings.cap.freeformOutbound")}: {caps.supports_freeform_outbound ? "yes" : "no"}</div>
                  <div>{t("manager.settings.cap.templateOnlyOutbound")}: {caps.template_based_outbound_only ? "yes" : "no"}</div>
                  <div>{t("manager.settings.cap.deepLinks")}: {caps.supports_deep_links ? "yes" : "no"}</div>
                  <div>{t("manager.settings.cap.identityLinking")}: {caps.supports_identity_linking ? "yes" : "no"}</div>
                </div>
              </div>
            ))
          )}
        </div>
      </section>
      <section className="panel p-4">
        <h2 className="text-lg font-semibold">{t("manager.settings.featureFlags")}</h2>
        <pre className="text-xs bg-slate-100 p-2 rounded mt-2 overflow-x-auto">
          {JSON.stringify(bootstrap?.feature_flags ?? {}, null, 2)}
        </pre>
      </section>
    </div>
  );
}

